// Central analytics event dispatcher for House of Stox.
//
// Events enter dataLayer for the site's tags and are forwarded directly to
// Mixpanel when allowlisted. Mobile click events also use the existing Google
// tag queue, bypassing the generic forwarder's property filter and debounce.
//
// Conventions:
//  - Event names are snake_case (GA4 requires reserved snake_case names to
//    populate ecommerce reports). Friendly display names live in the Mixpanel Lexicon.
//  - Buyer transactional spine uses GA4's RESERVED ecommerce events
//    (view_item, add_to_cart, begin_checkout, add_shipping_info,
//    add_payment_info, purchase) so Monetization reports + Google Ads populate.
//  - Marketplace-specific actions (place a bid/offer, create an ask/listing,
//    seller onboarding) are custom events layered on top.
//  - Ecommerce events always pair `value` + `currency` (AED), and carry the
//    GA4 items[] array; item-scoped fields (seller_id, size, condition) live
//    inside each item.

import { MOBILE_EVENTS } from "./mobileAnalytics.mjs";
import { stripLanguagePrefix } from "./storefrontLocale.mjs";
import { mpTrack } from "./mixpanel";

const SITE_CURRENCY = "AED";

// Product-analytics events worth sending to Mixpanel (skip the impression firehose
// — view_item_list / view_promotion / select_item — which is noisy + costs MTU).
// Which checkout surface an event came from, inferred from the route.
export function checkoutFlow() {
  const p = stripLanguagePrefix(window.location.pathname);
  if (p.startsWith("/buy/")) return "buy";
  if (p.startsWith("/makebid/")) return "bid";
  return "drawer";
}

const MIXPANEL_EVENTS = new Set([
  "buy_click", "offer_click",
  "view_item", "add_to_cart", "begin_checkout", "add_payment_info", "add_coupon",
  "purchase", "offer_start", "offer_submitted", "add_to_wishlist", "search",
  "select_size", "sell_start", "master_product_selected", "ask_size_selected",
  "ask_price_entered", "ask_created", "buyer_step_complete", "buyer_onboarding_complete",
  "seller_step_complete", "seller_onboarding_complete", "sign_up", "login",
  "language_switch",
  // checkout micro-steps (guest + member, both flows)
  "checkout_step_view", "guest_email_submitted", "guest_otp_verified",
  "guest_otp_failed", "otp_resend_clicked", "offer_price_entered",
  "address_saved", "card_added", "card_add_failed", "bid_blocked_no_card",
  "payment_failed",
]);

function forwardToMixpanel(event, props) {
  if (MIXPANEL_EVENTS.has(event) || (props?.tracking_version === "mobile_clicks_v1" && MOBILE_EVENTS.has(event))) mpTrack(event, props || {});
}

// Flatten a GA4 ecommerce object into flat Mixpanel props (Mixpanel prefers flat).
function ecommerceToProps(ecommerce = {}) {
  const first = (ecommerce.items && ecommerce.items[0]) || {};
  const out = {
    value: ecommerce.value,
    currency: ecommerce.currency,
    transaction_id: ecommerce.transaction_id,
    coupon: ecommerce.coupon,
    num_items: (ecommerce.items || []).length || undefined,
    item_id: first.item_id,
    item_name: first.item_name,
    item_brand: first.item_brand,
    item_category: first.item_category,
    price: first.price,
    size: first.size,
  };
  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
}

function getDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

// Auth state at event time — lets every funnel split logged-in vs guest
// (GA4: register `logged_in` as an event-scoped custom dimension; Mixpanel
// gets it as a normal event property).
function isLoggedIn() {
  try {
    return !!(localStorage.getItem("token") || localStorage.getItem("userData"));
  } catch (e) {
    return false;
  }
}

/**
 * Push a generic (non-ecommerce) event to the dataLayer.
 * Use for auth, engagement, marketplace bid/ask, and onboarding events.
 *
 * @param {string} event  snake_case event name (e.g. "ask_created")
 * @param {object} [params]  flat params (e.g. { item_id, size, ask_price })
 */
export function track(event, params = {}) {
  const dl = getDataLayer();
  if (!dl || !event) return;
  const logged_in = isLoggedIn();
  const mobile = params.tracking_version === "mobile_clicks_v1" && MOBILE_EVENTS.has(event);
  const payload = { logged_in, ...params };
  // The deployed generic forwarder respects __hosFwd. Preserve all mobile
  // properties and rapid repeated actions without sending the event twice.
  dl.push({ event, ...payload, ...(mobile ? { __hosFwd: true } : {}) });
  forwardToMixpanel(event, payload);
  if (mobile) {
    const gtag = window.gtag || function () { dl.push(arguments); };
    gtag("event", event, { ...payload, send_to: "G-55STZFSBDF" });
  }
}

/**
 * Push a GA4-style ecommerce event. Clears the previous `ecommerce` object
 * first (Google's recommended pattern) so item data never leaks between events.
 *
 * @param {string} event  reserved GA4 ecommerce event (e.g. "view_item", "purchase")
 * @param {object} ecommerce  { value, currency?, items:[...], transaction_id?, coupon?, ... }
 */
export function trackEcommerce(event, ecommerce = {}) {
  const dl = getDataLayer();
  if (!dl || !event) return;
  const logged_in = isLoggedIn();
  const payload = { currency: SITE_CURRENCY, ...ecommerce };
  dl.push({ ecommerce: null }); // clear prior ecommerce object
  dl.push({ event, logged_in, ecommerce: payload });
  forwardToMixpanel(event, { logged_in, ...ecommerceToProps(payload) });
}

/**
 * Build a single GA4 `items[]` entry from a House of Stox product/auction object.
 * Keeps item shape consistent across view_item, add_to_cart, purchase, etc.
 */
export function toItem(product = {}, extra = {}) {
  if (!product) return { ...extra };
  return {
    item_id: String(product.id ?? product.product_id ?? product.master_product_id ?? ""),
    item_name: product.product_name || product.model || product.title || product.name || "",
    item_brand: product.brand_name || product.brand || undefined,
    item_category: product.category_name || product.category || product.subtitle || undefined,
    price: Number(
      product.displayPrice ?? product.lowest_buy_now ?? product.price ?? product.ask_price ?? 0
    ) || undefined,
    quantity: 1,
    ...extra, // size, size_id, seller_id, condition, authentication_status, index, etc.
  };
}

export default track;
